import { coalesceAfterPaint } from '@/helpers/afterPaint';
import { selectors } from './constants.js';

export default {
    initSlider() {
        const { container } = this;
        if (!container) {
            return;
        }

        // Idempotent: tear down any previously created sliders before re-creating
        // them, so a second initSlider() call (e.g. the idle first-interaction
        // init racing a variant-change gallery rebuild) can't leave orphaned,
        // duplicated Swiper instances with leaked listeners. This is the single
        // teardown point — callers (applySliderHTML) no longer destroy separately.
        this.sliders?.mainSlider?.destroy();
        this.sliders?.mobileSlider?.destroy();
        this.sliders?.thumbsSlider?.destroy();

        const mainSlider = container?.querySelector(`[data-product-images-slider-desktop]`);
        if (!mainSlider) {
            return;
        }

        this.initThumbsSlider();

        const thumbs = this.sliders.thumbsSlider ? {
            swiper: this.sliders.thumbsSlider
        } : null;

        const pagination = document.querySelector('[data-product-images-pagination]');

        const swiperOptions = {
            initialSlide: 0,
            slidesPerView: 1,
            spaceBetween: 0,
            draggable: true,
            allowTouchMove: true,
            touchEventsTarget: 'container',
            keyboard: {
                enabled: true
            }
        };

        const desktopSwiper = new this.dependencies.Swiper(mainSlider, {
            ...swiperOptions,
            initialSlide: 0,
            loop: true,
            effect: 'fade',
            fadeEffect: {
                crossFade: true
            },
            zoom: {
                enabled: true,
                minRatio: 1,
                maxRatio: 1,
                toggle: true,
            },
            breakpoints: {
                1025: {
                    zoom: {
                        enabled: true,
                        minRatio: 1,
                        maxRatio: 1.75,
                        toggle: true
                    }
                }
            },
            thumbs,
            modules: [
                this.dependencies.Zoom,
                this.dependencies.Thumbs,
                this.dependencies.EffectFade,
                this.dependencies.Keyboard,
                this.dependencies.Pagination
            ],
            on: {
                afterInit(swiper) {
                    const element = swiper?.el;
                    if (!element) {
                        return;
                    }

                    element?.addEventListener('click', () => {
                        const zoom = swiper?.zoom;
                        if (!zoom) {
                            return;
                        }

                        zoom?.toggle();
                    });

                    const wrapperEl = swiper?.wrapperEl;
                    if (!wrapperEl) {
                        return;
                    }

                    const slides = swiper?.slides;
                    if (!slides) {
                        wrapperEl.dataset.slidesCount = '0';

                        return;
                    }

                    if (!slides?.length) {
                        wrapperEl.dataset.slidesCount = '0';

                        return;
                    }

                    const filteredSlides = slides?.filter((slide) => {
                        return !slide?.dataset?.swiperSlideDuplicate;
                    }) || [];

                    if (!filteredSlides) {
                        wrapperEl.dataset.slidesCount = '0';

                        return;
                    }

                    if (!filteredSlides?.length) {
                        wrapperEl.dataset.slidesCount = '0';

                        return;
                    }

                    wrapperEl.dataset.slidesCount = slides.length;
                },
                resize(swiper) {
                    try {
                        const zoom = swiper?.zoom;
                        if (!zoom) {
                            return;
                        }

                        zoom?.out();
                    } catch {}
                },
                sliderMove(swiper) {
                    try {
                        const zoom = swiper?.zoom;
                        if (!zoom) {
                            return;
                        }

                        zoom?.out();
                    } catch {}
                },
                slideChange(swiper) {
                    const slides = swiper?.slides;
                    if (!slides) {
                        return;
                    }

                    if (!slides?.length) {
                        return;
                    }

                    const activeSlide = slides[swiper.activeIndex];
                    if (activeSlide) {
                        const video = activeSlide?.querySelector('video[data-video-autoplay][muted]:not(.initialized)');

                        if (video) {
                            video?.play();
                            video?.classList?.add('initialized');
                        }
                    }

                    try {
                        const zoom = swiper?.zoom;
                        if (!zoom) {
                            return;
                        }

                        zoom?.out();
                    } catch {}
                }
            }
        });

        const mobileSlider = document.querySelector(`[data-product-images-slider-mobile]`);
        if (!mobileSlider) {
            return;
        }

        const mobileSwiper = new this.dependencies.Swiper(mobileSlider, {
            ...swiperOptions,
            initialSlide: 1,
            effect: 'slide',
            centeredSlides: true,
            spaceBetween: 4,
            ...(pagination ? {
                pagination: {
                    el: pagination,
                    type: 'custom',
                    clickable: true,
                    renderCustom(swiper, current, total) {
                        const slides = swiper?.slides;
                        if (!slides) {
                            return '';
                        }

                        if (!slides?.length) {
                            return '';
                        }

                        return [
                            ...Array(total).fill(0).map((item, index) => {
                                return index + 1;
                            })
                        ].map((index) => {
                            const slide = slides[index - 1];
                            if (!slide) {
                                return '';
                            }

                            let classes = [
                                "swiper-pagination-bullet",
                            ];

                            if (current === index) {
                                classes = [
                                    ...classes,
                                    "swiper-pagination-bullet-active",
                                ];
                            }

                            if (!![
                                index === 1,
                                index === total,
                            ]?.includes(true)) {
                                classes = [
                                    ...classes,
                                    "swiper-pagination-bullet--hidden"
                                ];
                            }

                            if (slide?.dataset?.slideType?.toLowerCase() === "video") {
                                classes = [
                                    ...classes,
                                    "swiper-pagination-bullet--video"
                                ];
                            }

                            return `<span
                                class="${classes.join(' ')}"
                                data-swiper-slide-index="${index}"
                                data-index="${index}"
                            ></span>`;
                        }).join('');
                    }
                }
            } : {}),
            modules: [
                this.dependencies.Pagination,
                this.dependencies.Keyboard,
                this.dependencies.Zoom,
            ],
            on: {
                afterInit(swiper) {
                    const wrapperEl = swiper?.wrapperEl;
                    if (!wrapperEl) {
                        return;
                    }

                    const slides = swiper?.slides;
                    if (!slides) {
                        wrapperEl.dataset.slidesCount = '0';

                        return;
                    }

                    if (!slides?.length) {
                        wrapperEl.dataset.slidesCount = '0';
                        swiper?.slideTo(0);

                        return;
                    }

                    const filteredSlides = slides?.filter((slide) => {
                        return !slide?.dataset?.swiperSlideDuplicate;
                    });

                    if (!filteredSlides) {
                        wrapperEl.dataset.slidesCount = '0';
                        swiper?.slideTo(0);

                        return;
                    }

                    if (!filteredSlides?.length) {
                        wrapperEl.dataset.slidesCount = '0';
                        swiper?.slideTo(0);

                        return;
                    }

                    wrapperEl.dataset.slidesCount = slides.length;
                },
                reachBeginning(swiper) {
                    const { slides } = swiper;
                    if (!slides) {
                        return;
                    }

                    const slidesCount = slides?.length;
                    if (!slidesCount) {
                        return;
                    }

                    swiper?.slideTo((slides?.length || 0) - 2);
                },
                reachEnd(swiper) {
                    const { slides } = swiper;
                    if (!slides) {
                        return;
                    }

                    const slidesCount = slides?.length;
                    if (!slidesCount) {
                        return;
                    }

                    swiper?.slideTo(1);
                },
                slideChange(swiper) {
                    const activeSlide = swiper.slides[swiper.activeIndex];
                    if (activeSlide) {
                        const video = activeSlide.querySelector('video[data-video-autoplay][muted]:not(.initialized)');

                        if (video) {
                            video?.play();
                            video?.classList?.add('initialized');
                        }
                    }

                    try {
                        const zoom = swiper?.zoom;
                        if (!zoom) {
                            return;
                        }

                        zoom?.out();
                    } catch {}
                },
            }
        });

        this.sliders.mainSlider = desktopSwiper;
        this.sliders.mobileSlider = mobileSwiper;

        const jsonData = document.querySelector(selectors.allImages);
        if (!jsonData) {
            return;
        }

        const parsedImages = JSON.parse(jsonData.innerHTML);
        if (!parsedImages) {
            return;
        }

        let images = '';
        let thumbnails = '';

        parsedImages?.allImages?.forEach((image, i) => {
            images += image;
            thumbnails += parsedImages?.allThumbs?.[i] || '';
        });

        this.originalImages = images;
        this.originalThumbnails = thumbnails;
    },

    initThumbsSlider() {
        const container = this.container;
        if (!container) {
            return;
        }

        const thumbsSlider = container?.querySelector(selectors?.thumbsSlider);
        if (!thumbsSlider) {
            // No thumbnail carousel for this variant. Clear any stale (possibly
            // already-destroyed) reference so initSlider doesn't pass a dead
            // Swiper as `thumbs` to the new main slider.
            this.sliders.thumbsSlider = null;
            return;
        }

        this.sliders.thumbsSlider = new this.dependencies.Swiper(thumbsSlider, {
            initialSlide: 0,
            direction: 'vertical',
            spaceBetween: 10,
            slidesPerView: 4,
            loop: false,
            draggable: true,
            allowTouchMove: true,
            navigation: {
                nextEl: '[data-product-thumbs-next]',
                prevEl: '[data-product-thumbs-prev]'
            },
            modules: [
                this.dependencies.Navigation
            ],
            on: {
                afterInit(swiper) {
                    const wrapperEl = swiper?.wrapperEl;
                    if (!wrapperEl) {
                        return;
                    }

                    const slides = swiper?.slides;
                    if (!slides) {
                        wrapperEl.dataset.slidesCount = '0';

                        return;
                    }

                    if (!slides?.length) {
                        wrapperEl.dataset.slidesCount = '0';

                        return;
                    }

                    const filteredSlides = slides?.filter((slide) => !slide?.dataset?.swiperSlideDuplicate);

                    if (!filteredSlides) {
                        wrapperEl.dataset.slidesCount = '0';

                        return;
                    }

                    if (!filteredSlides?.length) {
                        wrapperEl.dataset.slidesCount = '0';

                        return;
                    }

                    wrapperEl.dataset.slidesCount = slides?.length || 0;
                }
            }
        });
    },

    // Initialise the option carousels exactly once, lazily. Called from the idle
    // first-interaction init (desktop / fallback) AND synchronously when the
    // mobile options panel opens — so the Swipers exist before the panel is shown
    // (otherwise the slides render unstyled/collapsed until idle eventually fires).
    ensureOptionSlidersInited() {
        if (this.optionSlidersInited) {
            return;
        }

        this.optionSlidersInited = true;

        this.initOptionsSliders();
        this.initOptionItemSliders();
    },

    initOptionsSliders() {
        const { container } = this;
        if (!container) {
            return;
        }

        const optionsSliders = container?.querySelectorAll(selectors?.optionsSlider);
        if (!optionsSliders?.length) {
            return;
        }

        // Idempotent: destroy any previously created option sliders before
        // recreating, so calling this more than once (idle init + just-in-time
        // init on panel open) can't orphan Swiper instances.
        this.sliders.optionsSliders?.forEach((slider) => slider?.destroy());

        this.sliders.optionsSliders = [];

        optionsSliders.forEach((optionsSlider) => {
            const slider = optionsSlider?.querySelector('.swiper');
            if (!slider) {
                return;
            }

            let prevEl = null;
            let nextEl = null;
            let scrollbarEl = null;
            let paginationEl = null;

            if (this.isMobile) {
                prevEl = optionsSlider.querySelector('.options-slider__navigation [data-swiper-prev]');
                nextEl = optionsSlider.querySelector('.options-slider__navigation [data-swiper-next]');
                paginationEl = optionsSlider?.querySelector('.options-slider__navigation .swiper-pagination');
            } else {
                 prevEl = optionsSlider.querySelector('[data-swiper-prev]');
                 nextEl = optionsSlider.querySelector('[data-swiper-next]');
                 scrollbarEl = optionsSlider?.querySelector('.swiper-scrollbar');
            }

            this.sliders.optionsSliders.push(
                new this.dependencies.Swiper(slider, {
                    slidesPerView: 2.5,
                    initialSlide: 0,
                    centeredSlides: false,
                    spaceBetween: 8,
                    draggable: true,
                    allowTouchMove: true,
                    nested: true,
                    loop: false,
                    cssMode: this.isMobile,
                    modules: [
                        this.dependencies.Navigation,
                        this.dependencies.Scrollbar,
                        this.dependencies.Pagination
                    ],
                    navigation: {
                        nextEl,
                        prevEl
                    },
                    scrollbar: {
                        el: scrollbarEl,
                        draggable: true
                    },
                    pagination: {
                        el: paginationEl,
                        type: 'bullets',
                        clickable: true,
                    },
                    breakpoints: {
                        768: {
                            slidesPerView: 5,
                        },
                        1025: {
                            slidesPerView: 3,
                        },
                        1280: {
                            slidesPerView: 5,
                        }
                    }
                })
            );

        });
    },

    updateOptionsSliders() {
        if (!this.sliders?.optionsSliders?.length) {
            return;
        }

        // Swiper.update() forces a layout recalc per slider (~50-120ms across all
        // option sliders combined). It only re-measures slides after options were
        // shown/hidden, so run it after the next paint (coalesced) — keeping the
        // recalc out of the interaction's presentation-delay window so the
        // selection paints first and the sliders settle next.
        this.optionsSlidersUpdate = this.optionsSlidersUpdate || coalesceAfterPaint(() => {
            this.sliders?.optionsSliders?.forEach((slider) => {
                slider?.update();
            });
        });

        this.optionsSlidersUpdate();
    },

    initOptionItemSliders() {
        const { container } = this;
        if (!container) {
            return;
        }

        this.sliders.optionItemSlider.forEach((slider) => {
            return slider.destroy();
        });

        const optionItemSlider = Array.from(container?.querySelectorAll(selectors?.optionItemSlider));
        if (!optionItemSlider) {
            return;
        }

        if (!optionItemSlider?.length) {
            return;
        }

        this.sliders.optionItemSlider = optionItemSlider?.reduce((sliders, optionsSlider) => {
            const optionItem = optionsSlider?.closest('.option-item'); // <== direct parent .option-item
            if (!optionItem) {
                return sliders;
            }

            const slider = optionsSlider?.querySelector('.swiper');
            if (!slider) {
                return sliders;
            }

            const prevEl = optionItem?.querySelector('[data-swiper-prev]');
            const nextEl = optionItem?.querySelector('[data-swiper-next]');
            const paginationEl = optionsSlider?.querySelector('.swiper-pagination');

            let options = {
                slidesPerView: 1,
                spaceBetween: 0,
                draggable: false,
                allowTouchMove: false,
                nested: true,
                loop: false,
                modules: [],
            };

            if (prevEl && nextEl) {
                options = {
                    ...options,
                    modules: [
                        ...(options?.modules || []),
                        this.dependencies.Navigation,
                    ],
                    navigation: {
                        nextEl,
                        prevEl
                    },
                };
            }

            if (paginationEl) {
                options = {
                    ...options,
                    modules: [
                        ...(options?.modules || []),
                        this.dependencies.Pagination,
                    ],
                    pagination: {
                        el: paginationEl,
                        type: 'bullets',
                        clickable: true,
                        dynamicBullets: false,
                        mainDynamicBullets: 2,
                        dynamicBulletIndex: 0
                    },
                };
            }


            const swiper = new this.dependencies.Swiper(slider, options);

            sliders.push(swiper);

            return sliders;
        }, []) || [];
    },
};
